import { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import red from '@material-ui/core/colors/red';
import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import { buildImageUrl } from '../utils/images';
import { licenses } from '../utils/licenses';
import MaterialLink from './material-link';
import MaterialLinkButton from './material-link-button';
import { momentParse } from '../utils/time';

const useStyles = makeStyles((theme) => ({
  thumbnail: {
    height: 142,
    width: 142,
    display: 'block',
    margin: theme.spacing(0, 1, 1, 0),
  },
  mobileThumbnail: {
    height: 100,
    width: 100,
  },
  metaHeader: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
    '&:not(:first-child)': {
      marginTop: theme.spacing(2),
    },
  },
  user: {
    display: 'flex',
    alignItems: 'center',
  },
  createdAt: {
    marginTop: theme.spacing(0.5),
  },
  avatar: {
    marginRight: theme.spacing(0.5),
    width: 20,
    height: 20,
  },
  licenseBlock: {
    marginTop: theme.spacing(2),
  },
  license: {
    borderColor: '#ccc',
  },
  deleteButton: {
    marginTop: theme.spacing(2),
    color: `${red.A400} !important`,
    borderColor: `${red.A400} !important`,
  },
}));

const ImageUploadGalleryItem = ({ imageUpload, width: pageWidth }) => {
  const classes = useStyles();
  const {
    image,
    license,
    anonymous,
    createdByUser,
    permissions,
    status,
  } = imageUpload;

  const { width, height } = useMemo(() => {
    if (image.width > 3000 || image.height > 3000) {
      if (image.width > image.height) {
        return {
          height: image.height * 3000 / image.width,
          width: 3000,
        };
      } else {
        return {
          width: image.width * 3000 / image.height,
          height: 3000,
        };
      }
    }
    return image;
  }, [image]);


  // catch it if it got deleted recently
  if (status === 'DELETED') {
    return null;
  }
  
  const isMobile = isWidthDown('sm', pageWidth);
  const createdAt = useMemo(() => momentParse(imageUpload.createdAt), [imageUpload]);

  return (
    <div
      className={clsx('pswp-gallery__item', classes.thumbnail, { [classes.mobileThumbnail]: isMobile })}
    >
      <a
        href={buildImageUrl(image, 'gallery-large')}
        data-pswp-width={width}
        data-pswp-height={height}
        data-cropped="true"
      >
        <img src={buildImageUrl(image, `gallery-thumb-crop-${isMobile ? '100' : '142'}`)} />
      </a>
      <div className="pswp-caption-content">
        <div className={classes.metaHeader}>Uploaded by</div>
        {anonymous ? <div>Anonymous</div> : (
          <MaterialLink
            href="/people/[username]"
            as={`/people/${createdByUser.username}`}
            className={classes.user}
          >
            {createdByUser.avatar && (
              <Avatar
                alt={createdByUser.displayName}
                src={createdByUser.avatar.small.url}
                className={classes.avatar}
              />
            )}
            {createdByUser.displayName}
          </MaterialLink>
        )}
        <div className={classes.createdAt}>
          {createdAt.format('MMMM D, YYYY h:mm a')}
        </div>
        <div className={classes.licenseBlock}>
          <Chip
            clickable
            component="a"
            href={licenses[license].href}
            label={`License: ${licenses[license].shortLabel}`}
            title={licenses[license].label}
            variant="outlined"
            className={classes.license}
          />
        </div>
        {permissions.editable && (
          <MaterialLinkButton
            href="/image-uploads"
            className={classes.deleteButton}
            size="small"
            variant="outlined"
          >
            Delete
          </MaterialLinkButton>
        )}
      </div>
    </div>
  );
};

ImageUploadGalleryItem.propTypes = {
  imageUpload: PropTypes.object.isRequired,
  width: PropTypes.number,
};

export default withWidth()(ImageUploadGalleryItem);
